import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const sameCode = (/** @type {string} */ a) => String(a).trim().toUpperCase() === discountCodeValue.trim().toUpperCase();
      const appliedAsDiscount = (data.discount_codes || []).find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) => sameCode(discount.code) && discount.applicable === true
      );

      if (!appliedAsDiscount) {
        // Not a valid discount code: it may be a gift card. Gift cards can't go through
        // /cart/update.js, so we apply them to the same cart via the Storefront API.
        const giftCardApplied = await this.#tryGiftCard(discountCodeValue, existingDiscounts);
        discountCode.value = '';
        if (!giftCardApplied) this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#onCartChange = () => this.#refreshGiftCards();
    document.addEventListener('cart:update', this.#onCartChange);
    document.addEventListener('discount:update', this.#onCartChange);
    this.#refreshGiftCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onCartChange) {
      document.removeEventListener('cart:update', this.#onCartChange);
      document.removeEventListener('discount:update', this.#onCartChange);
    }
  }

  /** @type {(() => void) | null} */
  #onCartChange = null;

  /** @type {Array<{id: string, lastCharacters: string, amountUsed: {amount: string, currencyCode: string}}>} */
  #giftCards = [];

  /**
   * Calls the Storefront API (tokenless) on the shop's own domain.
   * @param {string} query
   * @param {Object} variables
   */
  async #storefront(query, variables) {
    const response = await fetch('/api/2025-10/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    return response.json();
  }

  /** Returns the Storefront API id of the current cart. */
  async #cartId() {
    const cart = await fetch(`${Theme.routes.cart_url || '/cart'}.js`).then((r) => r.json());
    return cart && cart.token ? `gid://shopify/Cart/${cart.token}` : null;
  }

  /**
   * Tries to apply a code as a gift card. Returns true when it was applied.
   * @param {string} code
   * @param {string[]} existingDiscounts
   */
  async #tryGiftCard(code, existingDiscounts) {
    try {
      const clean = code.replace(/[\s-]/g, '').toUpperCase();
      if (clean.length < 8) {
        await this.#restoreDiscounts(existingDiscounts);
        return false;
      }
      const cartId = await this.#cartId();
      if (!cartId) return false;
      const result = await this.#storefront(
        `mutation($id: ID!, $codes: [String!]!) {
          cartGiftCardCodesAdd(cartId: $id, giftCardCodes: $codes) {
            cart { appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } } }
            userErrors { message }
          }
        }`,
        { id: cartId, codes: [clean] }
      );
      const cards = result?.data?.cartGiftCardCodesAdd?.cart?.appliedGiftCards || [];
      const applied = cards.some((card) => clean.endsWith(String(card.lastCharacters).toUpperCase()));
      // Remove the code from the discount list again so checkout doesn't flag it as an invalid discount.
      await this.#restoreDiscounts(existingDiscounts);
      this.#giftCards = cards;
      this.#renderGiftCards();
      return applied;
    } catch (error) {
      return false;
    }
  }

  /**
   * Re-sends the previous discount list and re-renders the section.
   * @param {string[]} existingDiscounts
   */
  async #restoreDiscounts(existingDiscounts) {
    if (typeof this.dataset.sectionId !== 'string') return;
    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
    });
    const response = await fetch(Theme.routes.cart_update_url, config);
    const data = await response.json();
    if (data?.sections?.[this.dataset.sectionId]) {
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    }
  }

  /** Loads the gift cards already applied to the cart. */
  async #refreshGiftCards() {
    try {
      const cartId = await this.#cartId();
      if (!cartId) return;
      const result = await this.#storefront(
        `query($id: ID!) { cart(id: $id) { appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } } } }`,
        { id: cartId }
      );
      this.#giftCards = result?.data?.cart?.appliedGiftCards || [];
      this.#renderGiftCards();
    } catch (error) {}
  }

  /**
   * Removes an applied gift card.
   * @param {string} giftCardId
   */
  async #removeGiftCard(giftCardId) {
    try {
      const cartId = await this.#cartId();
      if (!cartId) return;
      const result = await this.#storefront(
        `mutation($id: ID!, $ids: [ID!]!) {
          cartGiftCardCodesRemove(cartId: $id, appliedGiftCardIds: $ids) {
            cart { appliedGiftCards { id lastCharacters amountUsed { amount currencyCode } } }
          }
        }`,
        { id: cartId, ids: [giftCardId] }
      );
      this.#giftCards = result?.data?.cartGiftCardCodesRemove?.cart?.appliedGiftCards || [];
      this.#renderGiftCards();
    } catch (error) {}
  }

  /** Paints the applied gift cards under the discount pills. */
  #renderGiftCards() {
    // The container may have been replaced by a section morph, so look it up every time.
    const list = this.closest('.shopify-section, body')?.querySelector('[data-gift-cards]') || this.querySelector('[data-gift-cards]');
    if (!(list instanceof HTMLElement)) return;
    const label = list.dataset.label || 'Tarjeta regalo';
    const note = list.dataset.note || '';
    const removeLabel = list.dataset.remove || 'Quitar';
    const locale = document.documentElement.lang || 'es';
    list.innerHTML = '';
    for (const card of this.#giftCards) {
      const amount = Number(card.amountUsed?.amount || 0);
      const money = new Intl.NumberFormat(locale, { style: 'currency', currency: card.amountUsed?.currencyCode || 'EUR' }).format(amount);
      const li = document.createElement('li');
      li.className = 'cart-discount__pill cart-discount__pill--gift';
      const text = document.createElement('p');
      text.className = 'cart-discount__pill-code';
      text.style.maxWidth = 'none';
      text.textContent = `${label} ···${card.lastCharacters} · −${money}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled';
      btn.setAttribute('aria-label', `${removeLabel} ${label} ···${card.lastCharacters}`);
      btn.textContent = '×';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.#removeGiftCard(card.id);
      });
      li.append(text, btn);
      list.append(li);
    }
    const noteEl = list.nextElementSibling;
    if (noteEl instanceof HTMLElement && noteEl.hasAttribute('data-gift-cards-note')) {
      noteEl.textContent = this.#giftCards.length ? note : '';
      noteEl.hidden = !this.#giftCards.length;
    }
    if (this.#giftCards.length) {
      const disclosure = this.closest('disclosure-custom');
      disclosure?.querySelector('[aria-expanded="false"]')?.click();
    }
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
